import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { motion, ease as mmEase, useInView } from "@/lib/motion";
import { CORRIDORS } from "../data";

const ez = mmEase.expo;

export default function CorridorsSection({ goToApp }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px 0px" });

  return (
    <section
      id="countries"
      ref={ref}
      className="relative py-24 px-6"
      style={{ background: "var(--color-section-subtle)", overflow: "hidden" }}
    >
      <div className="relative z-10 max-w-[1200px] mx-auto">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: ez }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10"
        >
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald mb-3">
              Africa first
            </p>
            <h2
              className="font-display font-black text-navy text-balance"
              style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)", letterSpacing: "-0.032em" }}
            >
              Send money<br />to Africa.
            </h2>
          </div>
          <button
            onClick={goToApp}
            className="hidden sm:flex items-center gap-2 text-[13px] font-semibold text-emerald hover:text-emerald-hover transition-colors shrink-0"
          >
            View all corridors <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        </motion.div>

        {/* The Route Arc — London to Lagos, draws itself on scroll */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.4, ease: ez }}
          className="relative mb-8"
          style={{ height: "72px" }}
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 800 72"
            fill="none"
            style={{ width: "100%", height: "72px", overflow: "visible" }}
          >
            <defs>
              <linearGradient id="co-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#10B981" />
                <stop offset="100%" stopColor="#06B6D4" />
              </linearGradient>
            </defs>
            <path
              d="M 40 58 C 200 14, 600 14, 760 58"
              stroke="url(#co-grad)"
              strokeWidth="1.5"
              strokeLinecap="round"
              fill="none"
              pathLength="1"
              style={{
                strokeDasharray: "1",
                strokeDashoffset: inView ? 0 : 1,
                transition: "stroke-dashoffset 1.1s cubic-bezier(0.16,1,0.3,1) 0.2s",
              }}
            />
            <circle cx="40"  cy="58" r="4" fill="#10B981"
              opacity={inView ? 0.7 : 0}
              style={{ transition: "opacity 0.4s ease 1.1s" }}
            />
            <circle cx="760" cy="58" r="4" fill="#06B6D4"
              opacity={inView ? 0.7 : 0}
              style={{ transition: "opacity 0.4s ease 1.1s" }}
            />
            <text x="40"  y="18" textAnchor="middle" fontSize="10" fontWeight="700"
              letterSpacing="0.1em" fill="#10B981"
              opacity={inView ? 0.65 : 0}
              style={{ fontFamily: "Inter, system-ui", transition: "opacity 0.4s ease 1.1s" }}
            >LON</text>
            <text x="760" y="18" textAnchor="middle" fontSize="10" fontWeight="700"
              letterSpacing="0.1em" fill="#06B6D4"
              opacity={inView ? 0.65 : 0}
              style={{ fontFamily: "Inter, system-ui", transition: "opacity 0.4s ease 1.1s" }}
            >LOS</text>
          </svg>
          {/* Traveling packet — starts after the arc finishes drawing */}
          {inView && (
            <svg
              viewBox="0 0 800 72"
              fill="none"
              aria-hidden="true"
              style={{ width: "100%", height: "72px", overflow: "visible", position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
            >
              <path id="co-path" d="M 40 58 C 200 14, 600 14, 760 58" fill="none" stroke="none" />
              <circle r="5" fill="#10B981" opacity="0.55">
                <animateMotion dur="2.8s" begin="1.4s" repeatCount="indefinite">
                  <mpath href="#co-path" />
                </animateMotion>
              </circle>
              <circle r="2.5" fill="white">
                <animateMotion dur="2.8s" begin="1.4s" repeatCount="indefinite">
                  <mpath href="#co-path" />
                </animateMotion>
              </circle>
            </svg>
          )}
        </motion.div>

        {/* Four corridor cards — flags, label, rate, time only */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {CORRIDORS.slice(0, 4).map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.48, ease: ez, delay: 0.3 + i * 0.06 }}
              className="card-float p-5 cursor-pointer"
              onClick={goToApp}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">{c.from}</span>
                <div style={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${c.color}45, ${c.color}12)` }} />
                <span className="text-2xl">{c.to}</span>
              </div>
              <p className="font-bold text-navy text-[13px] mb-1">{c.label}</p>
              <p className="text-[11px] font-mono num-tabular mb-3" style={{ color: "rgba(11,29,58,0.42)" }}>
                {c.rate}
              </p>
              <span
                className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full"
                style={{ background: `${c.color}12`, color: c.color }}
              >
                {c.time}
              </span>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
